// Blur / Sharpen / Smudge tools (R) — brush-based retouching tools.
//
// Blur: averages pixels in the brush area (Gaussian-like).
// Sharpen: enhances edges using unsharp mask principle.
// Smudge: drags pixels along the brush stroke direction.
//
// Photoshop parity:
// - Mode: blur / sharpen / smudge
// - Strength (1-100%)
// - Brush size, hardness, opacity
// - Sample All Layers (for smudge)

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import type { Layer, RasterLayer } from "../document/Layer";
import { findRaster, collectRasterLayers } from "../document/LayerUtils";
import { compositeLayer } from "../pixel/blend";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export type RetouchMode = "blur" | "sharpen" | "smudge";

export interface BrushSettings {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
}

/** Document-sized composite of every visible raster layer, used when Sample All Layers is on. */
interface CompositeBuffer {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

export class RetouchTool implements Tool {
  readonly id = "retouch";
  readonly shortcut = "R";

  mode: RetouchMode = "blur";
  strength = 50; // 0..100
  settings: BrushSettings = { size: 30, hardness: 0.5, opacity: 1, flow: 1 };
  /**
   * Smudge-only (Photoshop grays this out for Blur/Sharpen): when true, the
   * smudge samples its dragged color from a composite of every visible
   * layer instead of only the active layer — so smudging can pick up color
   * from layers below the one being painted on (audit finding 175).
   */
  sampleAllLayers = false;
  /**
   * Smudge-only "Finger Painting" checkbox: when true, each new stroke
   * starts by loading the foreground color into the smudge (as if dipping a
   * finger in paint first) instead of picking up whatever pixel is under the
   * cursor at stroke start (audit finding 175).
   */
  fingerPainting = false;
  /**
   * Options-bar "Mode" blend mode field (issue #81), added for parity with
   * CloneStampTool. Not yet consumed by applyEffect() below — blur/sharpen/
   * smudge always operate in plain (non-blend-mode) compositing.
   */
  blendMode = "normal";

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    // For smudge: stores the pixel color at the start of the stroke
    smudgeColor: { r: number; g: number; b: number; a: number } | null;
    selMask: { mask: Uint8Array | undefined; w: number | undefined };
    /**
     * Snapshot of every visible layer composited together, captured once at
     * stroke start when sampleAllLayers is on (smudge only). Sampling from a
     * live-mutating composite mid-stroke would be both expensive to
     * recompute per-dab and would let the smudge "see" its own just-painted
     * pixels on other layers; Photoshop's Sample All Layers freezes the
     * merged view for the duration of the stroke, same as here.
     */
    compositeSnapshot: CompositeBuffer | null;
    /**
     * True once the stroke's first dab has been applied. Finger Painting
     * only seeds the very first dab with the foreground color (Photoshop:
     * a fresh stroke starts with a dab of "loaded" color, then drags real
     * canvas content from there); every later dab samples normally.
     */
    smudgeFirstDabDone: boolean;
  } | null = null;

  /** Extract selection mask from context. */
  private getSelMask(ctx: ToolContext): { mask: Uint8Array | undefined; w: number | undefined } {
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    if (!sel || !sel.mask) return { mask: undefined, w: undefined };
    return { mask: sel.mask, w: ctx.doc!.width };
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    // Sample All Layers (smudge only): freeze a composite of every visible
    // layer for the duration of this stroke.
    const compositeSnapshot: CompositeBuffer | null =
      this.mode === "smudge" && this.sampleAllLayers && ctx.doc
        ? compositeVisibleRasterLayers(ctx.doc)
        : null;

    // For smudge, seed the starting color: Finger Painting loads the
    // foreground color directly; otherwise sample the pixel under the
    // cursor — from the composite when Sample All Layers is on, or just the
    // active layer otherwise.
    let smudgeColor: { r: number; g: number; b: number; a: number } | null = null;
    if (this.mode === "smudge") {
      if (this.fingerPainting) {
        smudgeColor = { r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b, a: 255 };
      } else if (compositeSnapshot) {
        smudgeColor = sampleComposite(compositeSnapshot, Math.round(p.x), Math.round(p.y));
      } else {
        const idx = this.pixelIndex(layer, docX, docY);
        if (idx !== null) {
          smudgeColor = {
            r: layer.pixels.data[idx]!,
            g: layer.pixels.data[idx + 1]!,
            b: layer.pixels.data[idx + 2]!,
            a: layer.pixels.data[idx + 3]!,
          };
        }
      }
    }

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
      smudgeColor,
      selMask: this.getSelMask(ctx),
      compositeSnapshot,
      smudgeFirstDabDone: false,
    };

    this.applyEffect(ctx, docX, docY, docX, docY);
    if (this.active) this.active.smudgeFirstDabDone = true;
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    // Apply effect along the line from last position
    const step = Math.max(1, this.settings.size / 4);
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));

    for (let i = 1; i <= n; i++) {
      const t = i / n;
      const cx = this.active.lastX + dx * t;
      const cy = this.active.lastY + dy * t;
      this.applyEffect(ctx, this.active.lastX + dx * ((i - 1) / n), this.active.lastY + dy * ((i - 1) / n), cx, cy);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }

    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private applyEffect(
    _ctx: ToolContext,
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
  ): void {
    if (!this.active) return;
    const layer = this.active.layer;
    const data = layer.pixels.data;
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    const radius = this.settings.size / 2;
    const hardness = this.settings.hardness;
    const strength = this.strength / 100;

    // Process at the midpoint of the stroke segment
    const cx = (fromX + toX) / 2;
    const cy = (fromY + toY) / 2;
    // Direction for smudge
    const dirX = toX - fromX;
    const dirY = toY - fromY;
    const dirLen = Math.hypot(dirX, dirY) || 1;

    const minX = Math.max(0, Math.floor(cx - radius));
    const minY = Math.max(0, Math.floor(cy - radius));
    const maxX = Math.min(w - 1, Math.ceil(cx + radius));
    const maxY = Math.min(h - 1, Math.ceil(cy + radius));

    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    const { mask: selMask, w: selMaskW } = this.active.selMask;

    // For blur/sharpen, we need a copy of the region to avoid feedback
    const regionW = maxX - minX + 1;
    const regionH = maxY - minY + 1;
    const regionData = new Uint8ClampedArray(regionW * regionH * 4);
    for (let ry = 0; ry < regionH; ry++) {
      for (let rx = 0; rx < regionW; rx++) {
        const srcIdx = ((minY + ry) * w + (minX + rx)) * 4;
        const dstIdx = (ry * regionW + rx) * 4;
        regionData[dstIdx] = data[srcIdx]!;
        regionData[dstIdx + 1] = data[srcIdx + 1]!;
        regionData[dstIdx + 2] = data[srcIdx + 2]!;
        regionData[dstIdx + 3] = data[srcIdx + 3]!;
      }
    }

    const r2 = radius * radius;

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - cy;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - cx;
        if (dx * dx + dy2 >= r2) continue;

        // Soft-edge falloff
        const dist = Math.sqrt(dx * dx + dy2) / radius;
        const brushAlpha = dist >= 1 ? 0 : dist > hardness ? (1 - dist) / (1 - hardness || 1e-6) : 1;
        let effectStrength = brushAlpha * strength;

        // Selection masking
        if (selMask && selMaskW) {
          const selVal = selMask[py * selMaskW + px] ?? 0;
          if (selVal === 0) continue;
          effectStrength *= selVal / 255;
        }

        if (effectStrength <= 0) continue;

        // Lock-transparency: skip fully transparent pixels.
        if (layer.lockTransparency) {
          const idx = (py * w + px) * 4;
          if (data[idx + 3] === 0) continue;
        }

        const idx = (py * w + px) * 4;
        const rx = px - minX;
        const ry = py - minY;

        if (this.mode === "blur") {
          this.applyBlur(data, regionData, idx, rx, ry, regionW, regionH, w, effectStrength);
        } else if (this.mode === "sharpen") {
          this.applySharpen(data, regionData, idx, rx, ry, regionW, regionH, w, effectStrength);
        } else if (this.mode === "smudge" && this.active.smudgeColor) {
          this.applySmudge(data, idx, px, py, dirX / dirLen, dirY / dirLen, radius, effectStrength);
        }

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    layer.pixels.markDirty();
  }

  private applyBlur(
    data: Uint8ClampedArray,
    region: Uint8ClampedArray,
    idx: number,
    rx: number,
    ry: number,
    rw: number,
    rh: number,
    fullW: number,
    strength: number,
  ): void {
    // Simple 3x3 box blur
    let r = 0, g = 0, b = 0, a = 0, count = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = rx + dx;
        const ny = ry + dy;
        if (nx < 0 || nx >= rw || ny < 0 || ny >= rh) continue;
        const nIdx = (ny * rw + nx) * 4;
        r += region[nIdx]!;
        g += region[nIdx + 1]!;
        b += region[nIdx + 2]!;
        a += region[nIdx + 3]!;
        count++;
      }
    }
    r /= count; g /= count; b /= count; a /= count;

    // Blend blurred result with original
    data[idx] = Math.round(data[idx]! * (1 - strength) + r * strength);
    data[idx + 1] = Math.round(data[idx + 1]! * (1 - strength) + g * strength);
    data[idx + 2] = Math.round(data[idx + 2]! * (1 - strength) + b * strength);
    data[idx + 3] = Math.round(data[idx + 3]! * (1 - strength) + a * strength);
  }

  private applySharpen(
    data: Uint8ClampedArray,
    region: Uint8ClampedArray,
    idx: number,
    rx: number,
    ry: number,
    rw: number,
    rh: number,
    _fullW: number,
    strength: number,
  ): void {
    // Unsharp mask: original + strength * (original - blurred)
    const centerR = region[(ry * rw + rx) * 4]!;
    const centerG = region[(ry * rw + rx) * 4 + 1]!;
    const centerB = region[(ry * rw + rx) * 4 + 2]!;

    // 3x3 blur
    let blurR = 0, blurG = 0, blurB = 0, count = 0;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        const nx = rx + dx;
        const ny = ry + dy;
        if (nx < 0 || nx >= rw || ny < 0 || ny >= rh) continue;
        const nIdx = (ny * rw + nx) * 4;
        blurR += region[nIdx]!;
        blurG += region[nIdx + 1]!;
        blurB += region[nIdx + 2]!;
        count++;
      }
    }
    blurR /= count; blurG /= count; blurB /= count;

    // Sharpen = center + strength * (center - blur)
    const amount = strength * 2; // Amplify for visible effect
    data[idx] = clampByte(centerR + (centerR - blurR) * amount);
    data[idx + 1] = clampByte(centerG + (centerG - blurG) * amount);
    data[idx + 2] = clampByte(centerB + (centerB - blurB) * amount);
    // Alpha unchanged
  }

  private applySmudge(
    data: Uint8ClampedArray,
    idx: number,
    px: number,
    py: number,
    dirX: number,
    dirY: number,
    radius: number,
    strength: number,
  ): void {
    if (!this.active) return;

    const layer = this.active.layer;
    const lw = layer.pixels.width;
    const lh = layer.pixels.height;

    // Smudge "pushes" pixels along the stroke: sample from the direction
    // we came FROM (opposite of movement direction), not where we're going.
    const sampleX = Math.round(px - dirX * radius * 0.5);
    const sampleY = Math.round(py - dirY * radius * 0.5);

    if (sampleX < 0 || sampleX >= lw || sampleY < 0 || sampleY >= lh) return;

    let srcR: number, srcG: number, srcB: number, srcA: number;
    const composite = this.active.compositeSnapshot;
    if (this.fingerPainting && !this.active.smudgeFirstDabDone && this.active.smudgeColor) {
      // Finger Painting: the stroke's very first dab paints the seeded
      // foreground color rather than sampling canvas content — matches
      // Photoshop's "dip your finger in paint first" behavior. Every
      // subsequent dab (smudgeFirstDabDone becomes true right after this
      // initial onPointerDown stamp) samples normally.
      srcR = this.active.smudgeColor.r;
      srcG = this.active.smudgeColor.g;
      srcB = this.active.smudgeColor.b;
      srcA = this.active.smudgeColor.a;
    } else if (composite) {
      // Sample All Layers: read from the frozen composite instead of just
      // this layer's pixels, translating layer-local coords to doc coords.
      const sampled = sampleComposite(composite, sampleX + layer.x, sampleY + layer.y);
      if (!sampled) return;
      srcR = sampled.r; srcG = sampled.g; srcB = sampled.b; srcA = sampled.a;
    } else {
      const sampleIdx = (sampleY * lw + sampleX) * 4;
      srcR = layer.pixels.data[sampleIdx]!;
      srcG = layer.pixels.data[sampleIdx + 1]!;
      srcB = layer.pixels.data[sampleIdx + 2]!;
      srcA = layer.pixels.data[sampleIdx + 3]!;
    }

    // Blend the sampled color into the current pixel
    data[idx] = Math.round(data[idx]! * (1 - strength) + srcR * strength);
    data[idx + 1] = Math.round(data[idx + 1]! * (1 - strength) + srcG * strength);
    data[idx + 2] = Math.round(data[idx + 2]! * (1 - strength) + srcB * strength);
    data[idx + 3] = Math.round(data[idx + 3]! * (1 - strength) + srcA * strength);

    // Update smudge color — the brush picks up the color it's dragging over
    if (this.active.smudgeColor) {
      this.active.smudgeColor = {
        r: Math.round(this.active.smudgeColor.r * 0.7 + srcR * 0.3),
        g: Math.round(this.active.smudgeColor.g * 0.7 + srcG * 0.3),
        b: Math.round(this.active.smudgeColor.b * 0.7 + srcB * 0.3),
        a: Math.round(this.active.smudgeColor.a * 0.7 + srcA * 0.3),
      };
    }
  }

  private pixelIndex(layer: RasterLayer, docX: number, docY: number): number | null {
    const px = Math.round(docX);
    const py = Math.round(docY);
    if (px < 0 || px >= layer.pixels.width || py < 0 || py >= layer.pixels.height) return null;
    return (py * layer.pixels.width + px) * 4;
  }
}

/**
 * Composite every visible raster layer of `doc` (bottom-to-top, in array
 * order — same convention the renderer uses) into a single document-sized
 * RGBA buffer. Used as the sampling source for Smudge's Sample All Layers.
 * This is a raster-only approximation (adjustment/shape/type layers aren't
 * folded in) — good enough for "what am I dragging paint from", the same
 * spirit as the eyedropper's compositeReader.
 */
function compositeVisibleRasterLayers(doc: { layers: readonly Layer[]; width: number; height: number }): CompositeBuffer {
  const w = doc.width;
  const h = doc.height;
  const out = new Uint8ClampedArray(w * h * 4);
  const rasters = collectRasterLayers(doc.layers);
  for (const layer of rasters) {
    if (!layer.visible || layer.opacity <= 0) continue;
    const lw = layer.pixels.width;
    const lh = layer.pixels.height;
    if (lw <= 0 || lh <= 0) continue;

    const x0 = Math.max(0, layer.x);
    const y0 = Math.max(0, layer.y);
    const x1 = Math.min(w, layer.x + lw);
    const y1 = Math.min(h, layer.y + lh);
    if (x1 <= x0 || y1 <= y0) continue;

    const placed = new Uint8ClampedArray(w * h * 4);
    for (let y = y0; y < y1; y++) {
      const srcRowStart = ((y - layer.y) * lw + (x0 - layer.x)) * 4;
      const dstRowStart = (y * w + x0) * 4;
      const rowLen = (x1 - x0) * 4;
      placed.set(layer.pixels.data.subarray(srcRowStart, srcRowStart + rowLen), dstRowStart);
    }
    compositeLayer(out, placed, layer.blendMode, layer.opacity);
  }
  return { data: out, width: w, height: h };
}

/** Sample an RGBA pixel from a composite buffer at integer doc coords, or null if out of bounds. */
function sampleComposite(buf: CompositeBuffer, x: number, y: number): { r: number; g: number; b: number; a: number } | null {
  if (x < 0 || y < 0 || x >= buf.width || y >= buf.height) return null;
  const i = (y * buf.width + x) * 4;
  return { r: buf.data[i]!, g: buf.data[i + 1]!, b: buf.data[i + 2]!, a: buf.data[i + 3]! };
}

function mergeBBox(a: BBox, b: BBox): BBox {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.max(a.x + a.w, b.x + b.w) - x;
  const h = Math.max(a.y + a.h, b.y + b.h) - y;
  return { x, y, w, h };
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
